import { useMemo, useState } from 'react';
import { useList, useGo } from '@refinedev/core';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { StatusBadge, getExpiryStatus } from '@/components/shared';
import { ImageIcon, Eye, MousePointerClick, ArrowRight, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { type Banner, getBannerBackgroundUrl } from '@/resources/banners/types';
import {
  type MiniBanner,
  getMiniBannerThumbnailUrl,
  getMiniBannerTitle,
} from '@/resources/mini-banners/types';
import type { StatusType } from '@/components/shared/status-badge';

interface ExpiryLike {
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
}

function getStatus(item: ExpiryLike): { status: StatusType; label?: string } {
  if (!item.isActive) {
    return { status: 'disabled', label: 'Выключен' };
  }
  const expiryStatus = getExpiryStatus(
    item.startDate ? new Date(item.startDate) : null,
    item.endDate ? new Date(item.endDate) : null,
  );
  return { status: expiryStatus };
}

function formatNumber(num: number): string {
  if (num >= 1000000) {return (num / 1000000).toFixed(1) + 'M';}
  if (num >= 1000) {return (num / 1000).toFixed(1) + 'K';}
  return num.toString();
}

interface BannersWidgetProps {
  className?: string;
}

type TabValue = 'banners' | 'mini-banners';

function isTabValue(value: string): value is TabValue {
  return value === 'banners' || value === 'mini-banners';
}

export function BannersWidget({ className }: BannersWidgetProps) {
  const go = useGo();
  const [tab, setTab] = useState<TabValue>('banners');

  const bannersQuery = useList<Banner>({
    resource: 'banners',
    pagination: { currentPage: 1, pageSize: 5, mode: 'server' },
    sorters: [{ field: 'order', order: 'asc' }],
  });

  const miniBannersQuery = useList<MiniBanner>({
    resource: 'mini-banners',
    pagination: { currentPage: 1, pageSize: 5, mode: 'server' },
    sorters: [{ field: 'order', order: 'asc' }],
  });

  const bannersData = bannersQuery.result?.data;
  const miniBannersData = miniBannersQuery.result?.data;

  const banners = useMemo(() => bannersData ?? [], [bannersData]);
  const miniBanners = useMemo(() => miniBannersData ?? [], [miniBannersData]);

  const bannersTotal = bannersQuery.result?.total ?? 0;
  const miniBannersTotal = miniBannersQuery.result?.total ?? 0;

  const isLoading = bannersQuery.query.isLoading || miniBannersQuery.query.isLoading;
  const currentTotal = tab === 'banners' ? bannersTotal : miniBannersTotal;
  const handleViewAll = tab === 'banners'
    ? () => go({ to: '/banners' })
    : () => go({ to: '/mini-banners' });

  const handleTabChange = (value: string) => {
    if (isTabValue(value)) {
      setTab(value);
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Images className="h-4 w-4" />
          Баннеры
        </CardTitle>
        <CardAction>
          <ViewAllButton total={currentTotal} onClick={handleViewAll} />
        </CardAction>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="banners" className="gap-2">
              Основные
              <Badge variant="secondary" className="h-5 px-1.5 tabular-nums">
                {bannersTotal}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="mini-banners" className="gap-2">
              Мини
              <Badge variant="secondary" className="h-5 px-1.5 tabular-nums">
                {miniBannersTotal}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="banners" className="mt-4">
            <BannersList
              isLoading={isLoading}
              banners={banners}
              onItemClick={(id) => go({ to: `/banners/edit/${id}` })}
              emptyText="Нет баннеров"
            />
          </TabsContent>

          <TabsContent value="mini-banners" className="mt-4">
            <MiniBannersList
              isLoading={isLoading}
              banners={miniBanners}
              onItemClick={(id) => go({ to: `/mini-banners/edit/${id}` })}
              emptyText="Нет мини-баннеров"
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

interface BannersListProps {
  isLoading: boolean;
  banners: readonly Banner[];
  emptyText: string;
  onItemClick: (id: number) => void;
}

function BannersList({ isLoading, banners, emptyText, onItemClick }: BannersListProps) {
  if (isLoading) {return <ListSkeleton />;}
  if (banners.length === 0) {return <EmptyState text={emptyText} />;}
  return (
    <div className="space-y-3">
      {banners.map((banner) => {
        const { status, label } = getStatus(banner);
        return (
          <Row
            key={banner.id}
            imageUrl={getBannerBackgroundUrl(banner)}
            title={`#${banner.id}`}
            status={status}
            statusLabel={label}
            viewsCount={banner.viewsCount}
            clicksCount={banner.clicksCount}
            onClick={() => onItemClick(banner.id)}
          />
        );
      })}
    </div>
  );
}

interface MiniBannersListProps {
  isLoading: boolean;
  banners: readonly MiniBanner[];
  emptyText: string;
  onItemClick: (id: number) => void;
}

function MiniBannersList({ isLoading, banners, emptyText, onItemClick }: MiniBannersListProps) {
  if (isLoading) {return <ListSkeleton />;}
  if (banners.length === 0) {return <EmptyState text={emptyText} />;}
  return (
    <div className="space-y-3">
      {banners.map((banner) => {
        const { status, label } = getStatus(banner);
        const title = getMiniBannerTitle(banner) ?? `#${banner.id}`;
        return (
          <Row
            key={banner.id}
            imageUrl={getMiniBannerThumbnailUrl(banner)}
            title={title}
            status={status}
            statusLabel={label}
            viewsCount={banner.viewsCount}
            clicksCount={banner.clicksCount}
            onClick={() => onItemClick(banner.id)}
          />
        );
      })}
    </div>
  );
}

interface RowProps {
  imageUrl: string | null;
  title: string;
  status: StatusType;
  statusLabel?: string;
  viewsCount: number;
  clicksCount: number;
  onClick: () => void;
}

function Row({ imageUrl, title, status, statusLabel, viewsCount, clicksCount, onClick }: RowProps) {
  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 rounded-md p-1.5 -mx-1.5 hover:bg-muted/50 transition-colors text-left"
      onClick={onClick}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          className="h-10 w-20 object-cover rounded flex-shrink-0"
        />
      ) : (
        <div className="h-10 w-20 bg-muted rounded flex items-center justify-center flex-shrink-0">
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{title}</span>
          <StatusBadge status={status} customLabel={statusLabel} size="sm" />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
          <span className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            {formatNumber(viewsCount)}
          </span>
          <span className="flex items-center gap-1">
            <MousePointerClick className="h-3 w-3" />
            {formatNumber(clicksCount)}
          </span>
        </div>
      </div>
    </button>
  );
}

const SKELETON_KEYS = ['a', 'b', 'c'] as const;

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {SKELETON_KEYS.map((key) => (
        <div key={key} className="flex items-center gap-3">
          <Skeleton className="h-10 w-20 rounded" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-[120px]" />
            <Skeleton className="h-3 w-[80px]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-sm text-muted-foreground text-center py-4">{text}</p>;
}

function ViewAllButton({ total, onClick }: { total: number; onClick: () => void }) {
  if (total === 0) {return null;}
  return (
    <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-muted-foreground" onClick={onClick}>
      Все
      <Badge variant="secondary" className="h-5 px-1.5 text-[10px] tabular-nums">
        {total}
      </Badge>
      <ArrowRight className="ml-1 h-3 w-3" />
    </Button>
  );
}
